import * as XLSX from "xlsx";
import { useState } from "react";
import { TextField, Button, Typography, Grid, Box, Paper, IconButton,Tooltip } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UploadIcon from "@mui/icons-material/Upload";
import { fieldConfig, getInitialState, labelToNameMap } from "./DefaultParameters";
import { ChevronRight, ChevronLeft } from "@mui/icons-material";
export default function Parameters({ parameters, setParameters }) {

    const [dragActive, setDragActive] = useState(false);
    const [leftExpanded, setLeftExpanded] = useState(false);
    //  SMART PARSER (supports = : tab csv)
    const parseSmart = (text) => {
        const lines = text.split("\n");
        const updated = {};

        lines.forEach((line, index) => {
            if (!line.trim()) return;

            // detect separator
            let parts;
            if (line.includes("=")) parts = line.split("=");
            else if (line.includes(":")) parts = line.split(":");
            else if (line.includes("\t")) parts = line.split("\t");
            else if (line.includes(",")) parts = line.split(",");
            else return;

            if (parts.length < 2) return;

            const rawKey = parts[0].trim();
            const value = parts.slice(1).join("").trim();

            const mappedName =
                labelToNameMap[rawKey.toLowerCase()] || rawKey;

            updated[mappedName] = value;
        });

        return updated;
    };

    // fallback (order-based)
    const parseByOrder = (text) => {
        const lines = text.split("\n");
        const updated = {};

        fieldConfig.forEach((field, index) => {
            if (lines[index]?.trim()) {
                updated[field.name] = lines[index].trim();
            }
        });

        return updated;
    };

    const handleParse = () => {
        const parsed =
            parameters.TXBParse.includes("=") ||
                parameters.TXBParse.includes(":") ||
                parameters.TXBParse.includes("\t") ||
                parameters.TXBParse.includes(",")
                ? parseSmart(parameters.TXBParse)
                : parseByOrder(parameters.TXBParse);

        setParameters(prev => ({ ...prev, ...parsed }));
    };

    const handleReset = () => {
        setParameters(getInitialState());
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setParameters(prev => ({ ...prev, [name]: value }));
    };

    // FILE PROCESS
    const processFile = (file) => {

        if (!file) return;

        const name = file.name.toLowerCase();
        const ext = name.split(".").pop().toLowerCase();

        // =====================================
        // VALIDATION
        // =====================================

        if (
            ext !== "csv" &&
            ext !== "txt" &&
            ext !== "xlsx" &&
            ext !== "xls"
        ) {
            alert(
                "Unsupported file type. Please use TXT, CSV, XLS or XLSX."
            );
            return;
        }

        // =====================================
        // FORMAT TEXT FOR TEXTAREA
        // =====================================

        const formatTabularText = (rows) => {

            if (!rows?.length) return "";

            const colWidths = [];

            rows.forEach((row) => {

                row.forEach((cell, i) => {

                    const len = String(
                        cell ?? ""
                    ).length;

                    colWidths[i] = Math.max(
                        colWidths[i] || 0,
                        len
                    );
                });
            });

            return rows
                .map((row) =>
                    row
                        .map((cell, i) =>
                            String(cell ?? "")
                                .padEnd(colWidths[i] + 4)
                        )
                        .join("")
                )
                .join("\n");
        };

        // =====================================
        // TXT / CSV
        // =====================================

        if (
            name.endsWith(".txt") ||
            name.endsWith(".csv")
        ) {

            const reader = new FileReader();

            reader.onload = (evt) => {

                const text = evt.target.result;

                // textarea formatted
                const rows = text
                    .split(/\r?\n/)
                    .map(line =>
                        line.split(",")
                    );

                const formattedText =
                    formatTabularText(rows);

                

                setParameters(prev => ({
                    ...prev,
                    TXBParse: formattedText
                }));

                const parsed = parseSmart(text);

                setParameters(prev => ({
                    ...prev,
                    ...parsed
                }));
            };

            reader.readAsText(file);
        }

        // =====================================
        // XLS / XLSX
        // =====================================

        else if (
            name.endsWith(".xls") ||
            name.endsWith(".xlsx")
        ) {

            const reader = new FileReader();

            reader.onload = (evt) => {

                const data = new Uint8Array(
                    evt.target.result
                );

                const workbook = XLSX.read(
                    data,
                    { type: "array" }
                );

                const sheet =
                    workbook.Sheets[
                    workbook.SheetNames[0]
                    ];

                const json =
                    XLSX.utils.sheet_to_json(
                        sheet,
                        { header: 1 }
                    );

                // =====================================
                // UPDATE TEXTAREA
                // =====================================

                const formattedText =
                    formatTabularText(json);

                

                setParameters(prev => ({
                    ...prev,
                    TXBParse: formattedText
                }));

                // =====================================
                // UPDATE PARAMETERS
                // =====================================

                const parsed = {};

                json.forEach((row) => {

                    if (row[0] && row[1]) {

                        const key = row[0]
                            .toString()
                            .trim()
                            .toLowerCase();

                        const mappedName =
                            labelToNameMap[key] ||
                            row[0];

                        parsed[mappedName] =
                            row[1].toString();
                    }
                });

                setParameters(prev => ({
                    ...prev,
                    ...parsed
                }));
            };

            reader.readAsArrayBuffer(file);
        }
    };
 
    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    };

    return (
        <Paper elevation={0} sx={{ border: "none", width: "100%" }}>
            {/* ================= MAIN LAYOUT ================= */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                    width: "100%",

                    height: {
                        xs: "auto",
                        lg: "45vh",
                    },

                    minHeight: {
                        xs: 260,
                        sm: 300,
                        md: 340,
                    },
                }}
            >
                {/* ================= LEFT PANEL ================= */}
                <Box
                    sx={{
                        width: {
                            xs: "100%",
                            md: leftExpanded ? "40%" : "40px",
                        },
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        position: "relative",
                        transition: "all 0.3s ease",
                        overflow: "hidden",
                        borderRight: leftExpanded ? "1px solid #eee" : "none",
                    }}
                >
                    {/* ================= TOGGLE BUTTON ================= */}
                
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                          
                            left: leftExpanded ? "auto" : 0,
                            right: leftExpanded ? 0 : "auto",
                            mr:2,
                            zIndex: 20,
                        }}
                    >
                        <Tooltip
                            title={
                                leftExpanded
                                    ? "Collapse Input Panel"
                                    : "Expand Input Panel"
                            }
                            arrow
                            placement={leftExpanded ? "left" : "right"}
                        >                           
                            <IconButton
                                size="small"
                                onClick={() => setLeftExpanded((prev) => !prev)}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    border: "1px solid #ddd",
                                    backgroundColor: "#774def",
                                    color:"white",
                                    "&:hover": {
                                        backgroundColor: "#774def",
                                        color: "white",
                                    },
                                }}
                            >
                                {leftExpanded ? <ChevronLeft /> : <ChevronRight />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                   
                    {/* ================= INPUT AREA ================= */}
                    {leftExpanded && (
                        <Box
                            position="relative"
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragActive(true);
                            }}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActive(false);
                                handleDrop(e);
                            }}
                            sx={{
                                border: dragActive
                                    ? "2px dashed #1976d2"
                                    : "1px solid #ddd",
                                borderRadius: 1,
                                p: 1,
                                flex: 1,
                                mt:7,
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: dragActive
                                    ? "rgba(25,118,210,0.05)"
                                    : "transparent",
                            }}
                        >
                            <TextField
                                name="TXBParse"
                                value={parameters.TXBParse}
                                onChange={handleChange}
                                multiline
                                fullWidth
                                placeholder={
                                    `Example:
Math Version:0
Min B0:-9999
Max B0:9999`
                                }
                                InputProps={{
                                    disableUnderline: true
                                }}
                                inputProps={{
                                    style: {
                                        fontFamily: "monospace",
                                        whiteSpace: "pre",
                                        overflowWrap: "normal"
                                    }
                                }}
                                sx={{
                                    flex: 1,
                                    "& .MuiOutlinedInput-root": {
                                        height: "100%",
                                        alignItems: "flex-start",
                                    },
                                    "& textarea": {
                                        height: "100% !important",
                                        overflowY: "auto !important",
                                    },
                                }}
                            />

                            {/* OVERLAY */}
                            {!parameters.TXBParse && (
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    width="100%"
                                    height="100%"
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        pointerEvents: "none",
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography variant="subtitle1">
                                        Drag & drop, paste or enter values.
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        component="label"
                                        startIcon={<UploadFileIcon />}
                                        sx={{ mt: 1, pointerEvents: "auto" }}
                                    >
                                        Upload File
                                        <input
                                            type="file"
                                            hidden
                                            accept=".txt,.csv,.xls,.xlsx"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) processFile(file);
                                            }}
                                        />
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* ================= RIGHT PANEL ================= */}
                <Box
                    sx={{
                        width: "100%",
                        flex: leftExpanded ? "0 0 60%" : "1 1 100%",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        transition: "all 0.3s ease",
                        position: "relative",
                    }}
                >
                    {/* PARAMETER GRID */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            p: 1,
                            border: "1px solid #ddd",
                            borderRadius: 1,
                        }}
                    >
                        <Grid container spacing={2}>
                            {fieldConfig.map((field) => (
                                <Grid item xs={12} sm={6} md={4} key={field.name}>
                                    <TextField
                                        label={field.label}
                                        name={field.name}
                                        value={parameters[field.name]}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Grid>
                            ))}

                            <Grid item xs={12}>
                                <TextField
                                    label="afWetResponseAbsoluteErrorMax"
                                    name="afWetResponseAbsoluteErrorMax"
                                    value={parameters.afWetResponseAbsoluteErrorMax}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Box>

            {/* ================= ACTIONS (HIDDEN WHEN COLLAPSED) ================= */}
            {leftExpanded && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={handleParse}
                            disabled={!parameters.TXBParse?.trim()}
                        >
                            Update
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            onClick={handleReset}
                            disabled={!parameters.TXBParse?.trim()}
                        >
                            Reset
                        </Button>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}